@file:Suppress("ktlint:standard:no-wildcard-imports")

package xyz.malefic.multipage.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronRight
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import xyz.malefic.multipage.components.Footer
import xyz.malefic.multipage.components.Header
import xyz.malefic.multipage.components.StickyContactBar
import xyz.malefic.multipage.i18n.t
import xyz.malefic.multipage.styles.Theme

@Page
@Composable
fun HomePage() {
    Column(
        modifier =
            Modifier
                .fillMaxSize()
                .backgroundColor(Theme.Background),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Header()
        HeroSection()
        AboutSection()
        ServicesSection()
        StatementSection()
        QualificationsSection()
        ContactSection()
        GoldCtaBand()
        Footer()
        StickyContactBar()
    }
}

// ─── 1. HERO ──────────────────────────────────────────────────────────────────

@Composable
private fun HeroSection() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .minHeight(580.px)
                .position(Position.Relative)
                .overflow(Overflow.Hidden)
                .backgroundColor(rgba(245, 243, 240, 1.0f)),
        contentAlignment = Alignment.CenterStart,
    ) {
        // Background photo (full cover)
        Image(
            src = "https://images.unsplash.com/photo-1540518614846-7eded433c457?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
            modifier =
                Modifier
                    .fillMaxSize()
                    .objectFit(ObjectFit.Cover)
                    .position(Position.Absolute),
            alt = "",
        )

        // White gradient overlay — fades left-to-right so text is readable on the left
        Box(
            modifier =
                Modifier
                    .fillMaxSize()
                    .classNames("ls-hero-overlay")
                    .position(Position.Absolute),
        )

        // Text content — left-aligned on top of gradient
        Column(
            modifier =
                Modifier
                    .position(Position.Relative)
                    .fillMaxWidth()
                    .maxWidth(1200.px)
                    .padding(leftRight = 48.px, topBottom = 80.px),
        ) {
            // Small label
            SpanText(
                text = t("brand.tagline"),
                modifier =
                    Modifier
                        .classNames("ls-tagline")
                        .fontSize(13.px)
                        .color(Theme.TextSecondary)
                        .letterSpacing(2.px)
                        .margin(bottom = 20.px),
            )

            // Main heading
            SpanText(
                text = t("hero.title"),
                modifier =
                    Modifier
                        .classNames("ls-hero-title", "ls-preline")
                        .fontFamily(Theme.HeadingFontFamily)
                        .fontSize(48.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Theme.Primary)
                        .lineHeight(1.4)
                        .margin(bottom = 24.px)
                        .maxWidth(520.px),
            )

            // Subtitle
            SpanText(
                text = t("hero.subtitle"),
                modifier =
                    Modifier
                        .classNames("ls-hero-subtitle", "ls-preline")
                        .fontSize(14.px)
                        .color(Theme.TextSecondary)
                        .lineHeight(2.0)
                        .margin(bottom = 40.px)
                        .maxWidth(640.px),
            )

        }
    }
}

// ─── 2. ABOUT SPLIT ───────────────────────────────────────────────────────────

@Composable
private fun AboutSection() {
    val ctx = rememberPageContext()
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .backgroundColor(Theme.Surface),
        contentAlignment = Alignment.Center,
    ) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(1200.px)
                    .flexWrap(FlexWrap.Wrap),
        ) {
            // Left: photo
            Box(
                modifier =
                    Modifier
                        .classNames("ls-split-right")
                        .minHeight(440.px)
                        .overflow(Overflow.Hidden),
            ) {
                Image(
                    src = "https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80",
                    modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover),
                    alt = "Modern interior",
                )
            }

            // Right: text
            Column(
                modifier =
                    Modifier
                        .classNames("ls-split-left")
                        .padding(topBottom = 72.px, leftRight = 56.px),
                verticalArrangement = Arrangement.Center,
            ) {
                SpanText(
                    text = t("about.story.p1"),
                    modifier =
                        Modifier
                            .fontFamily(Theme.HeadingFontFamily)
                            .fontSize(20.px)
                            .fontWeight(FontWeight.Bold)
                            .color(Theme.TextPrimary)
                            .lineHeight(1.8)
                            .margin(bottom = 20.px),
                )

                SpanText(
                    text = t("about.story.p2"),
                    modifier =
                        Modifier
                            .fontSize(14.px)
                            .color(Theme.TextSecondary)
                            .lineHeight(2.0)
                            .margin(bottom = 32.px),
                )

                Row(
                    modifier =
                        Modifier
                            .padding(leftRight = 20.px, topBottom = 11.px)
                            .border(1.5.px, LineStyle.Solid, Theme.Primary)
                            .borderRadius(3.px)
                            .cursor(Cursor.Pointer)
                            .onClick { ctx.router.navigateTo("/about") },
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center,
                ) {
                    SpanText(
                        text = t("about.home.cta"),
                        modifier =
                            Modifier
                                .fontSize(13.px)
                                .fontWeight(FontWeight.Bold)
                                .color(Theme.Primary)
                                .margin(right = 8.px),
                    )
                    Box(modifier = Modifier.color(Theme.Primary).fontSize(11.px)) { FaChevronRight() }
                }
            }
        }
    }
}

// ─── 3. SERVICES ──────────────────────────────────────────────────────────────

@Composable
private fun ServicesSection() {
    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 80.px)
                .backgroundColor(Theme.Background),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SectionHeading(
            label = "",
            title = t("services.title"),
            subtitle = t("services.subtitle"),
        )

        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(1200.px)
                    .margin(top = 52.px)
                    .padding(leftRight = 16.px)
                    .gap(24.px)
                    .flexWrap(FlexWrap.Wrap),
            horizontalArrangement = Arrangement.Center,
        ) {
            val ctx = rememberPageContext()
            ServiceCard(
                photoUrl = "https://images.unsplash.com/photo-1568605114967-8130f3a36994?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=260&q=80",
                title = t("services.ihinseiri.title"),
                description = t("services.ihinseiri.desc"),
                onClick = { ctx.router.navigateTo("/services") },
            )
            ServiceCard(
                photoUrl = "https://images.unsplash.com/photo-1560185007-cde436f6a4d0?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=260&q=80",
                title = t("services.akiya.title"),
                description = t("services.akiya.desc"),
                onClick = { ctx.router.navigateTo("/services") },
            )
            ServiceCard(
                photoUrl = "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=260&q=80",
                title = t("services.zaimu.title"),
                description = t("services.zaimu.desc"),
                onClick = { ctx.router.navigateTo("/services") },
            )
        }
    }
}

@Composable
private fun ServiceCard(
    photoUrl: String,
    title: String,
    description: String,
    onClick: () -> Unit,
) {
    Column(
        modifier =
            Modifier
                .classNames("ls-card")
                .backgroundColor(Theme.Surface)
                .borderRadius(8.px)
                .border(1.px, LineStyle.Solid, Theme.BorderLight)
                .boxShadow(offsetY = 2.px, blurRadius = 12.px, spreadRadius = 0.px, color = Theme.ShadowColor)
                .overflow(Overflow.Hidden)
                .cursor(Cursor.Pointer)
                .onClick { onClick() },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .height(180.px)
                    .overflow(Overflow.Hidden),
        ) {
            Image(
                src = photoUrl,
                modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover),
                alt = "",
            )
        }
        Column(
            modifier = Modifier.fillMaxWidth().padding(24.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                text = title,
                modifier =
                    Modifier
                        .margin(bottom = 10.px)
                        .fontSize(16.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Theme.TextPrimary)
                        .textAlign(TextAlign.Center),
            )
            SpanText(
                text = description,
                modifier =
                    Modifier
                        .fontSize(13.px)
                        .color(Theme.TextSecondary)
                        .textAlign(TextAlign.Center)
                        .lineHeight(1.8),
            )
            SpanText(
                text = "詳しくはこちら →",
                modifier =
                    Modifier
                        .margin(top = 14.px)
                        .fontSize(12.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Theme.Primary)
                        .letterSpacing(0.5.px),
            )
        }
    }
}

// ─── 4. STATEMENT ─────────────────────────────────────────────────────────────

@Composable
private fun StatementSection() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .backgroundColor(Theme.Surface),
        contentAlignment = Alignment.Center,
    ) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(1200.px)
                    .flexWrap(FlexWrap.Wrap),
        ) {
            // Left: large quote text
            Column(
                modifier =
                    Modifier
                        .classNames("ls-split-left")
                        .padding(topBottom = 80.px, leftRight = 56.px)
                        .backgroundColor(Theme.SurfaceVariant),
                verticalArrangement = Arrangement.Center,
            ) {
                Box(
                    modifier =
                        Modifier
                            .width(32.px)
                            .height(3.px)
                            .backgroundColor(Theme.Secondary)
                            .margin(bottom = 24.px),
                )
                SpanText(
                    text = t("statement.label"),
                    modifier =
                        Modifier
                            .fontSize(11.px)
                            .fontWeight(FontWeight.Bold)
                            .color(Theme.TextTertiary)
                            .letterSpacing(3.px)
                            .margin(bottom = 20.px),
                )
                SpanText(
                    text = t("statement.quote"),
                    modifier =
                        Modifier
                            .classNames("ls-statement-quote", "ls-preline")
                            .fontFamily(Theme.HeadingFontFamily)
                            .fontSize(24.px)
                            .fontWeight(FontWeight.Bold)
                            .color(Theme.TextPrimary)
                            .lineHeight(2.0),
                )
            }

            // Right: dark photo
            Box(
                modifier =
                    Modifier
                        .classNames("ls-split-right")
                        .minHeight(480.px)
                        .overflow(Overflow.Hidden)
                        .position(Position.Relative),
            ) {
                Image(
                    src = "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80",
                    modifier =
                        Modifier
                            .position(Position.Absolute)
                            .top(0.px)
                            .left(0.px)
                            .fillMaxSize()
                            .objectFit(ObjectFit.Cover),
                    alt = "",
                )
            }
        }
    }
}

// ─── 5. QUALIFICATIONS (light background) ─────────────────────────────────────

@Composable
private fun QualificationsSection() {
    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 80.px)
                .backgroundColor(Theme.Surface),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SectionHeading(
            label = "LIVSTAYJAPAN",
            title = t("qual.title"),
            subtitle = t("qual.subtitle"),
        )

        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(1100.px)
                    .margin(top = 52.px)
                    .padding(leftRight = 24.px)
                    .gap(24.px)
                    .flexWrap(FlexWrap.Wrap),
            horizontalArrangement = Arrangement.Center,
        ) {
            QualCard("🎖", t("qual.1.name"), t("qual.1.desc"))
            QualCard("🏢", t("qual.2.name"), t("qual.2.desc"))
            QualCard("📋", t("qual.3.name"), t("qual.3.desc"))
            QualCard("🤝", t("qual.4.name"), t("qual.4.desc"))
        }
    }
}

@Composable
private fun QualCard(
    icon: String,
    name: String,
    desc: String,
) {
    Column(
        modifier =
            Modifier
                .classNames("ls-card")
                .padding(28.px)
                .backgroundColor(Theme.Surface)
                .borderRadius(8.px)
                .border(1.px, LineStyle.Solid, Theme.BorderLight)
                .boxShadow(offsetY = 2.px, blurRadius = 10.px, spreadRadius = 0.px, color = Theme.ShadowColor),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(text = icon, modifier = Modifier.fontSize(34.px).margin(bottom = 14.px))
        SpanText(
            text = name,
            modifier =
                Modifier
                    .fontSize(14.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Theme.TextPrimary)
                    .textAlign(TextAlign.Center)
                    .margin(bottom = 10.px),
        )
        SpanText(
            text = desc,
            modifier =
                Modifier
                    .fontSize(12.px)
                    .color(Theme.TextSecondary)
                    .textAlign(TextAlign.Center)
                    .lineHeight(1.7),
        )
    }
}

// ─── 6. GOLD CTA BAND ─────────────────────────────────────────────────────────

@Composable
private fun GoldCtaBand() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 48.px)
                .backgroundColor(Theme.Secondary),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(800.px)
                    .padding(leftRight = 24.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                text = t("goldcta.text"),
                modifier =
                    Modifier
                        .classNames("ls-cta-title")
                        .fontFamily(Theme.HeadingFontFamily)
                        .fontSize(28.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Theme.Surface)
                        .textAlign(TextAlign.Center)
                        .margin(bottom = 8.px)
                        .letterSpacing(1.px),
            )
            SpanText(
                text = t("goldcta.sub"),
                modifier =
                    Modifier
                        .fontSize(14.px)
                        .color(rgba(255, 255, 255, 0.85f))
                        .textAlign(TextAlign.Center)
                        .margin(bottom = 32.px),
            )
            Box(
                modifier =
                    Modifier
                        .classNames("ls-gold-btn")
                        .padding(leftRight = 40.px, topBottom = 16.px)
                        .backgroundColor(Theme.Surface)
                        .borderRadius(4.px)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            js("window.location.href = 'mailto:info@livstayjapan.co.jp'")
                        },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    text = t("goldcta.btn"),
                    modifier =
                        Modifier
                            .fontWeight(FontWeight.Bold)
                            .color(Theme.Secondary)
                            .fontSize(15.px)
                            .letterSpacing(0.5.px),
                )
            }
        }
    }
}

// ─── 7. CONTACT SECTION ───────────────────────────────────────────────────────

@Composable
private fun ContactSection() {
    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 80.px)
                .backgroundColor(Theme.Background),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SectionHeading(
            label = "",
            title = t("contact.section.title"),
            subtitle = t("contact.section.desc"),
        )

        // Three property photos
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(1100.px)
                    .margin(top = 48.px)
                    .padding(leftRight = 16.px)
                    .gap(20.px)
                    .flexWrap(FlexWrap.Wrap),
            horizontalArrangement = Arrangement.Center,
        ) {
            ContactPhotoCard(
                "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400&h=240&fit=crop",
                t("services.ihinseiri.title"),
            )
            ContactPhotoCard(
                "https://images.unsplash.com/photo-1560185007-cde436f6a4d0?w=400&h=240&fit=crop",
                t("services.akiya.title"),
            )
            ContactPhotoCard(
                "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=240&fit=crop",
                t("services.zaimu.title"),
            )
        }

    }
}

@Composable
private fun ContactPhotoCard(
    photoUrl: String,
    label: String,
) {
    Column(
        modifier =
            Modifier
                .classNames("ls-card")
                .overflow(Overflow.Hidden)
                .borderRadius(8.px)
                .border(1.px, LineStyle.Solid, Theme.BorderLight)
                .boxShadow(offsetY = 2.px, blurRadius = 8.px, spreadRadius = 0.px, color = Theme.ShadowColor),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .height(180.px)
                    .overflow(Overflow.Hidden),
        ) {
            Image(
                src = photoUrl,
                modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover),
                alt = label,
            )
        }
        Box(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(16.px)
                    .backgroundColor(Theme.Surface),
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                text = label,
                modifier =
                    Modifier
                        .fontSize(13.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Theme.TextPrimary)
                        .textAlign(TextAlign.Center),
            )
        }
    }
}

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────

@Composable
fun SectionHeading(
    title: String,
    subtitle: String = "",
    label: String = "",
) {
    Column(
        modifier = Modifier.padding(leftRight = 24.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        if (label.isNotEmpty()) {
            SpanText(
                text = label,
                modifier =
                    Modifier
                        .fontSize(11.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Theme.Secondary)
                        .letterSpacing(3.px)
                        .margin(bottom = 12.px),
            )
        }

        SpanText(
            text = title,
            modifier =
                Modifier
                    .classNames("ls-section-title")
                    .fontFamily(Theme.HeadingFontFamily)
                    .fontSize(32.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Theme.TextPrimary)
                    .textAlign(TextAlign.Center)
                    .letterSpacing(1.px),
        )

        Box(
            modifier =
                Modifier
                    .margin(top = 14.px, bottom = 18.px)
                    .width(40.px)
                    .height(3.px)
                    .backgroundColor(Theme.Secondary)
                    .borderRadius(2.px),
        )

        if (subtitle.isNotEmpty()) {
            SpanText(
                text = subtitle,
                modifier =
                    Modifier
                        .maxWidth(600.px)
                        .fontSize(15.px)
                        .color(Theme.TextSecondary)
                        .textAlign(TextAlign.Center)
                        .lineHeight(1.9),
            )
        }
    }
}
